import React from 'react';
import { formatNumberWithCommas, getAbbreviationForPi } from '../../utilities';

const cellBorder = { border: '1px solid #000', padding: '4px 6px 4px 12px' };

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

// Generic inline-editable wrapper. `as` lets you pick the tag (span/div/td/etc).
const EditableField = ({ editMode, as: Tag = 'span', style, children, ...rest }) => (
  <Tag
    contentEditable={editMode}
    suppressContentEditableWarning={true}
    style={{
      outline: editMode ? '1px dashed #3b82f6' : 'none',
      backgroundColor: editMode ? 'rgba(59,130,246,0.06)' : 'transparent',
      minWidth: editMode ? '10px' : undefined,
      cursor: editMode ? 'text' : 'inherit',
      ...style,
    }}
    {...rest}
  >
    {children}
  </Tag>
);

const BeneficiaryCertificate = ({ table_info, commercial_info, documentTitle, editMode = false }) => {

  const formatDate = (unixSeconds) => {
    if (!unixSeconds) return '';
    return new Date(unixSeconds * 1000)
      .toLocaleString('en-GB', {
        timeZone: 'Asia/Dhaka',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '.');
  };

  const amendmentFields = [
    { label: 'Number of Amendment:', value: commercial_info.amendmentInfo },
    {
      label: 'DATED:',
      value: !isBlank(commercial_info.amendmentDate) ? formatDate(commercial_info.amendmentDate) : null,
    },
  ].filter((field) => !isBlank(field.value));

  const exportScFields = [
    { label: 'HS CODE NO:', value: '4819.10.00' },
    { label: 'APPLICANT BIN:', value: commercial_info.partyBin },
    { label: 'APPLICANT BOND LIC. NO.:', value: commercial_info.partyBondLicNo },
    { label: 'APPLICANT BOI NO.:', value: commercial_info.partyBoiNo },
    { label: 'BANK BIN:', value: commercial_info.partyBankBin },
    { label: 'APPLICANT ERC:', value: commercial_info.partyErc },
    { label: 'APPLICANT IRC:', value: commercial_info.partyIrc },
    { label: 'BENEFICIARY BIN:', value: '000686131-1205' },
  ].filter((field) => !isBlank(field.value));

  const lcNoText = (
    <>
      <strong>{commercial_info.lcNo}</strong>, DATED: {formatDate(commercial_info.lcDate)}
      {amendmentFields.length > 0 && ', '}
      {amendmentFields.map((field, index) => (
        <React.Fragment key={field.label}>
          {field.label} {field.value}
          {index < amendmentFields.length - 1 ? ', ' : ''}
        </React.Fragment>
      ))}
    </>
  );

  const exportScNoText = (
    <>
      {commercial_info.contractNo} DT.{formatDate(commercial_info.contractDate)}
      {exportScFields.length > 0 && ', '}
      {exportScFields.map((field, index) => (
        <React.Fragment key={field.label}>
          {field.label} {field.value}
          {index < exportScFields.length - 1 ? ', ' : ''}
        </React.Fragment>
      ))}.
    </>
  );

  const piNoText = (
    <>
      {commercial_info.piInfo &&
        commercial_info.piInfo.map((pi, index) => (
          <span key={pi.piId}>
            <strong>{pi.piNo || `PI/${getAbbreviationForPi(commercial_info.partyName)}/${pi.jobNumber}`}</strong> DT.{formatDate(pi.createdDate)}
            {index < commercial_info.piInfo.length - 1 ? ', ' : ''}
          </span>
        ))}
      <br />
      TRADE TERM: CPT APPLICANT'S FACTORY (INCOTERM 2020)
    </>
  );

  return (
    <div style={{ paddingTop: '10px', display: 'block', fontSize: '13px', lineHeight: '1.4' }}>
      <style>{`
        @media print {
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
      `}</style>

      <EditableField
        editMode={editMode}
        as="p"
        style={{
          textAlign: 'center',
          textDecoration: 'underline',
          fontSize: '23px',
          fontWeight: 'bold',
          margin: '0 0 10px',
          display: 'block',
        }}
      >
        {documentTitle}
      </EditableField>

      {/* Bordered summary table */}
      <table
        width="100%"
        border="1"
        cellSpacing="0"
        cellPadding="4"
        style={{ borderCollapse: 'collapse', marginBottom: '18px', fontSize: '13px' }}
      >
        <tbody>
          <tr>
            <td colSpan="2" style={cellBorder}>
              <EditableField editMode={editMode} as="span">
                We do here by certify that the all terms and conditions of the following.
              </EditableField>
            </td>
          </tr>
          <tr>
            <td colSpan="2" style={cellBorder}>
              <EditableField editMode={editMode} as="span">
                INVOICE NO: <strong>{commercial_info.invoiceNo}</strong>, DATE-{formatDate(commercial_info.commercialDate)}
              </EditableField>
            </td>
          </tr>
          <tr>
            <td width="140" valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">LC NO</EditableField>
            </td>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">{lcNoText}</EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">EXPORT S/C NO</EditableField>
            </td>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">{exportScNoText}</EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">PI NO</EditableField>
            </td>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">{piNoText}</EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">NOTIFIED TO</EditableField>
            </td>
            <td valign="top" style={cellBorder}>
              <EditableField editMode={editMode} as="span">
                {commercial_info.partyBank},<br />
                {commercial_info.partyBankAddress}
              </EditableField>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Numbered list restating the same details */}
      <table width="100%" border="0" cellSpacing="0" cellPadding="2" style={{ fontSize: '13px' }}>
        <tbody>
          <tr>
            <td width="25" valign="top">01.</td>
            <td width="160" valign="top">COMMODITY</td>
            <td width="10" valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} as="div">
                ACCESSORIES FOR 100% EXPORT ORIENTED READY MADE GERMENTS INDUSTRIES.
              </EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">02.</td>
            <td valign="top">APPLICANT</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} as="div">
                {table_info[0]?.party_name}<br />
                {table_info[0]?.party_address}
              </EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">03.</td>
            <td valign="top">L/C VALUE</td>
            <td valign="top">:</td>
            <td valign="top">
              <strong>
                <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                  USD {formatNumberWithCommas(commercial_info.totalPrice)}
                </EditableField>
              </strong>
            </td>
          </tr>
          <tr>
            <td valign="top">04.</td>
            <td valign="top">TRANSPORT</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                BY TRUCK
              </EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">05.</td>
            <td valign="top">LATEST DATE OF SHIPMENT</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                {commercial_info.expiredDate
                  ? formatDate(commercial_info.expiredDate)
                  : ''}
              </EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">06.</td>
            <td valign="top">L/C NO</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} as="div">{lcNoText}</EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">07.</td>
            <td valign="top">EXPORT S/C NO</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} as="div">{exportScNoText}</EditableField>
            </td>
          </tr>
          <tr>
            <td valign="top">08.</td>
            <td valign="top">PI NO</td>
            <td valign="top">:</td>
            <td valign="top">
              <EditableField editMode={editMode} as="div">{piNoText}</EditableField>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Signature block */}
      <table width="100%" border="0" cellSpacing="0" cellPadding="0" style={{ marginTop: '35px', fontSize: '11px', visibility: 'hidden' }}>
        <tbody>
          <tr>
            <td width="55%" valign="top">&nbsp;</td>
            <td align="right" valign="top">
              <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
                For, Miah and Sons Industries Limited
              </EditableField>
              <br /><br /><br />
              <EditableField
                editMode={editMode}
                as="span"
                style={{ borderTop: '1px solid black', paddingTop: '4px', display: 'inline-block' }}
              >
                Authorize Signature
              </EditableField>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default BeneficiaryCertificate;